import { useNavigate, useLocation } from 'react-router-dom';
import Card from '../components/card/Card';
import Icon from '../components/icon/Icon';
import NavBar from './NavBar';
import Tooltip from '../components/tooltip/Tooltip';
import { routes } from '../routes/routePaths';
import useRole from '../hooks/useRole';
import { useClientTheme } from '../context/ThemeContext';
import './Layout.scss';
import { shouldShowClientsModuleInSidebar } from '../utils/clientModuleAccess';
import {
  buildSidebarNavStyleInput,
  resolveActiveNavTileStyle,
  resolveActiveNavTileVariant,
  resolveNavIconVariant,
} from '../utils/sidebarNavStyle';

interface LayoutProps {
  children: React.ReactNode;
}

interface RoleIcon {
  width: number;
  height: number;
  name: string;
  route: string;
  msg: string;
  matchRoutes?: (string | RegExp)[];
}

const config = JSON.parse(localStorage.getItem('config') ?? '{}');
const isClientEnabled = config?.isClientEnabled ?? false;
const isGuaranteesEnabled = config?.isGuaranteesEnabled ?? false;
const isQuotationsEnabled = config?.isQuotationsEnabled ?? false;
const isInstrumentationEnabled = config?.isInstrumentationEnabled ?? true;

const COMMON_ICONS: RoleIcon[] = [
  {
    name: 'home_outline',
    width: 24,
    height: 24,
    route: routes.dashboardComercial,
    msg: 'Dashboard',
    matchRoutes: [routes.dashboardComercial, '/dashboard'],
  },
  {
    name: 'origination_outline',
    width: 24,
    height: 24,
    route: routes.dashboard,
    msg: 'Originacion',
    matchRoutes: [routes.dashboard, routes.clients],
  },
  {
    name: 'risk_outline',
    width: 24,
    height: 24,
    route: routes.risksAnalysis,
    msg: 'Análisis de riesgos',
    matchRoutes: [routes.risksAnalysis],
  },
  {
    name: 'committee_outline',
    width: 24,
    height: 24,
    route: routes.committee,
    msg: 'Comité',
    matchRoutes: [routes.committee],
  },
  {
    name: 'instrumentation_outline',
    width: 40,
    height: 40,
    route: routes.instrumentation,
    msg: 'Instrumentación',
    matchRoutes: [routes.instrumentation],
  },
  {
    name: 'folder_outline',
    width: 24,
    height: 24,
    route: routes.finishedFolders,
    msg: 'Carpetas finalizadas',
    matchRoutes: [routes.finishedFolders],
  },
];

const USERS_ICON: RoleIcon = {
  name: 'users_outline',
  width: 24,
  height: 24,
  route: routes.users,
  msg: 'Usuarios',
  matchRoutes: [routes.users],
};

const CLIENT_ICON: RoleIcon[] = [
  {
    name: 'users_3_outline',
    width: 24,
    height: 24,
    route: routes.clientsList,
    msg: 'Clientes',
    matchRoutes: [
      routes.clientsList,
      routes.clientDetail,
      new RegExp(`^${routes.clients}/[^/]+/application/[^/]+$`),
    ],
  },
];

const GUARANTEES_ICON: RoleIcon[] = [
    {
    name: 'protector_icon',
    width: 20,
    height: 20,
    route: routes.fundsAndProtectors,
    msg: 'Fondos y protectores',
    matchRoutes: [routes.fundsAndProtectors],
  },
  {
    name: 'monetization',
    width: 24,
    height: 24,
    route: routes.monetization,
    msg: 'Monetización',
    matchRoutes: [new RegExp(`^${routes.monetization}$`), routes.newMonetization, routes.editMonetization],
  },
  {
    name: 'search_eye',
    width: 24,
    height: 24,
    route: routes.guarantees,
    msg: 'Gestión de avales',
    matchRoutes: [routes.guarantees, routes.guaranteeDetail, routes.productLineDetail, routes.checkDetail, routes.loanDetail, routes.promissoryNoteDetail],
  },
  {
    name: 'overdue',
    width: 24,
    height: 24,
    route: routes.overdue,
    msg: 'Vencimientos',
    matchRoutes: [routes.overdue],
  },
  {
    name: 'annexes',
    width: 24,
    height: 24,
    route: routes.annexes,
    msg: 'Anexos',
    matchRoutes: [routes.annexes],
  },
  {
    name: 'reportes',
    width: 24,
    height: 24,
    route: routes.reportes,
    msg: 'Reportes',
    matchRoutes: [routes.reportes],
  },
];

const SIMULATOR_ICON: RoleIcon = {
  name: 'calculator_outline',
  width: 24,
  height: 24,
  route: routes.loanInstallmentSimulator,
  msg: 'Simulador de cuotas',
  matchRoutes: [routes.loanInstallmentSimulator],
};

const SETTINGS_ICON: RoleIcon = {
  name: 'settings_outline',
  width: 24,
  height: 24,
  route: routes.settings,
  msg: 'Configuración',
  matchRoutes: [routes.settings, routes.rateTypes, routes.sgr, routes.partners, routes.shares, routes.funds, routes.commissions, routes.regulacionSepyme, routes.referrers],
};


const producerIcons: RoleIcon[] = [
  {
    name: 'origination_outline',
    width: 24,
    height: 24,
    route: routes.dashboard,
    msg: 'Inicio',
    matchRoutes: [routes.dashboard],
  },
];

const roleIcons = {
  producer: producerIcons,
  commercial: COMMON_ICONS,
  comite: COMMON_ICONS,
  instrumentator: COMMON_ICONS,
};

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isProducer, isCommercial, isComite, isInstrumentator, isAdmin, isRiskAnalyst, isCommitteeOnly, canViewInstrumentation } = useRole();
  const { clientTheme } = useClientTheme();
  const sidebarNav = (isActive: boolean) => buildSidebarNavStyleInput(isActive, clientTheme);

  const COMMITTEE_ONLY_ICONS: RoleIcon[] = [
    COMMON_ICONS.find((i) => i.route === routes.committee)!,
  ];

  const userIcons: RoleIcon[] = [];
  if (isCommitteeOnly) {
    userIcons.push(...COMMITTEE_ONLY_ICONS);
  } else {
    if (isProducer) userIcons.push(...roleIcons.producer);
    if (isCommercial || isRiskAnalyst) userIcons.push(...roleIcons.commercial);
    if (isComite) userIcons.push(...roleIcons.comite);
    if (isInstrumentator) userIcons.push(...roleIcons.instrumentator);
    if (isAdmin) {
      userIcons.push(...roleIcons.commercial);
    }
  }

  return (
    <div 
      style={{ 
        display: 'grid', 
        height: '100vh', 
        width: '100%', 
        maxWidth: '100vw', 
        overflow: 'hidden'
      }}
    >
      <Card width='-webkit-fill-available' maxWidth='100vw'>
        <NavBar />
        <Card
          display='flex'
          height='calc(100vh - 58px)'
          style={
            clientTheme
              ? { backgroundColor: '#F6F7FA' }
              : undefined
          }
        >
          <Card
            variant='neutral'
            width='72px'
            borderRadius={false}
            modifier='inverted'
            justifyItems='center'
            spacing={{ top: '4' }}
          >
            <Card display='grid' gap='10px'>
              {userIcons.map((icon) => {
                // Skip instrumentation icon if not enabled
                if (icon.name === 'instrumentation_outline' && (!isInstrumentationEnabled || !canViewInstrumentation)) {
                  return null;
                }

                const isActive =
                  icon.matchRoutes?.some((route) => {
                    if (route instanceof RegExp) {
                      return route.test(location.pathname);
                    }
                    if (route === routes.clients) {
                      return location.pathname === route;
                    }
                    if (route === routes.clientDetail) {
                      return location.pathname.startsWith('/clientes/') && 
                             !location.pathname.startsWith('/clientes/list');
                    }
                    return location.pathname.startsWith(route);
                  }) ?? false;

                return (
                  <Card key={icon.name}>
                    <Card
                      display='flex'
                      variant={resolveActiveNavTileVariant(sidebarNav(isActive))}
                      modifier='weak'
                      height='36px'
                      justifyContent='center'
                      width='36px'
                      alignItems='center'
                      style={resolveActiveNavTileStyle(sidebarNav(isActive))}
                    >
                      <Card>
                        <Tooltip position='right' msg={icon.msg}>
                          <Icon
                            name={icon.name}
                            variant={resolveNavIconVariant(sidebarNav(isActive))}
                            modifier='strong'
                            width={icon.width}
                            height={icon.height}
                            cursor='pointer'
                            onClick={() => navigate(icon.route)}
                          />
                        </Tooltip>
                      </Card>
                    </Card>
                  </Card>
                );
              })}
              
              {!isCommitteeOnly && isGuaranteesEnabled && (isCommercial || isComite || isInstrumentator) &&  (
                <>
                  <div style={{ 
                    borderTop: '1px solid #E0E0E0',
                    width: '100%'
                  }} />
                  {GUARANTEES_ICON.map((icon) => {
                    const isActive =
                      icon.matchRoutes?.some((route) => {
                        if (route instanceof RegExp) {
                          return route.test(location.pathname);
                        }
                        if (route === routes.clients) {
                          return location.pathname === route;
                        }
                        if (route === routes.clientDetail) {
                          return location.pathname.startsWith('/clientes/') && 
                                 !location.pathname.startsWith('/clientes/list');
                        }
                        return location.pathname.startsWith(route);
                      }) ?? false;

                    return (
                      <Card key={icon.route}>
                        <Card
                          display='flex'
                          variant={resolveActiveNavTileVariant(sidebarNav(isActive))}
                          modifier='weak'
                          height='36px'
                          justifyContent='center'
                          width='36px'
                          alignItems='center'
                          style={resolveActiveNavTileStyle(sidebarNav(isActive))}
                        >
                          <Card>
                            <Tooltip position='right' msg={icon.msg}>
                              <Icon
                                name={icon.name}
                                variant={resolveNavIconVariant(sidebarNav(isActive))}
                                modifier='strong'
                                width={icon.width}
                                height={icon.height}
                                cursor='pointer'
                                onClick={() => navigate(icon.route)}
                              />
                            </Tooltip>
                          </Card>
                        </Card>
                      </Card>
                    );
                  })}
                  <div style={{ 
                    borderTop: '1px solid #E0E0E0', 
                    width: '100%'
                  }} />
                </>
              )}

{!isCommitteeOnly && isGuaranteesEnabled && (isCommercial || isComite || isInstrumentator) && (
                <Card key={SIMULATOR_ICON.name}>
                  <Card
                    display='flex'
                    variant={resolveActiveNavTileVariant(
                      sidebarNav(location.pathname.startsWith(SIMULATOR_ICON.route)),
                    )}
                    modifier='weak'
                    height='36px'
                    justifyContent='center'
                    width='36px'
                    alignItems='center'
                    style={resolveActiveNavTileStyle(
                      sidebarNav(location.pathname.startsWith(SIMULATOR_ICON.route)),
                    )}
                  >
                    <Card>
                      <Tooltip position='right' msg={SIMULATOR_ICON.msg}>
                        <Icon
                          name={SIMULATOR_ICON.name}
                          variant={resolveNavIconVariant(
                            sidebarNav(location.pathname.startsWith(SIMULATOR_ICON.route)),
                          )}
                          modifier='strong'
                          width={SIMULATOR_ICON.width}
                          height={SIMULATOR_ICON.height}
                          cursor='pointer'
                          onClick={() => navigate(SIMULATOR_ICON.route)}
                        />
                      </Tooltip>
                    </Card>
                  </Card>
                </Card>
              )}

              {!isCommitteeOnly && shouldShowClientsModuleInSidebar(isClientEnabled, {
                isCommercial,
                isComite,
                isInstrumentator,
                isAdmin,
                isRiskAnalyst,
              }) && CLIENT_ICON.map((icon) => {
                const isActive =
                  icon.matchRoutes?.some((route) => {
                    if (route instanceof RegExp) {
                      return route.test(location.pathname);
                    }
                    if (route === routes.clients) {
                      return location.pathname === route;
                    }
                    if (route === routes.clientDetail) {
                      return location.pathname.startsWith('/clientes/') && 
                             !location.pathname.startsWith('/clientes/list');
                    }
                    return location.pathname.startsWith(route);
                  }) ?? false;

                return (
                  <Card key={icon.name}>
                    <Card
                      display='flex'
                      variant={resolveActiveNavTileVariant(sidebarNav(isActive))}
                      modifier='weak'
                      height='36px'
                      justifyContent='center'
                      width='36px'
                      alignItems='center'
                      style={resolveActiveNavTileStyle(sidebarNav(isActive))}
                    >
                      <Card>
                        <Tooltip position='right' msg={icon.msg}>
                          <Icon
                            name={icon.name}
                            variant={resolveNavIconVariant(sidebarNav(isActive))}
                            modifier='strong'
                            width={icon.width}
                            height={icon.height}
                            cursor='pointer'
                            onClick={() => navigate(icon.route)}
                          />
                        </Tooltip>
                      </Card>
                    </Card>
                  </Card>
                );
              })}

              {!isCommitteeOnly && (isAdmin || isQuotationsEnabled || isGuaranteesEnabled) && (() => {
                const settingsActive =
                  SETTINGS_ICON.matchRoutes?.some((route) =>
                    typeof route === 'string' ? location.pathname.startsWith(route) : false,
                  ) ?? false;
                return (
                <Card key={SETTINGS_ICON.name}>
                  <Card
                    display='flex'
                    variant={resolveActiveNavTileVariant(sidebarNav(settingsActive))}
                    modifier='weak'
                    height='36px'
                    justifyContent='center'
                    width='36px'
                    alignItems='center'
                    style={resolveActiveNavTileStyle(sidebarNav(settingsActive))}
                  >
                    <Card>
                      <Tooltip position='right' msg={SETTINGS_ICON.msg}>
                        <Icon
                          name={SETTINGS_ICON.name}
                          variant={resolveNavIconVariant(sidebarNav(settingsActive))}
                          modifier='strong'
                          width={SETTINGS_ICON.width}
                          height={SETTINGS_ICON.height}
                          cursor='pointer'
                          onClick={() => navigate(SETTINGS_ICON.route)}
                        />
                      </Tooltip>
                    </Card>
                  </Card>
                </Card>
                );
              })()}

              {!isCommitteeOnly && (isAdmin || isComite) && (() => {
                const usersActive =
                  USERS_ICON.matchRoutes?.some((route) =>
                    typeof route === 'string' ? location.pathname.startsWith(route) : false,
                  ) ?? false;
                return (
                <Card key={USERS_ICON.name}>
                  <Card
                    display='flex'
                    variant={resolveActiveNavTileVariant(sidebarNav(usersActive))}
                    modifier='weak'
                    height='36px'
                    justifyContent='center'
                    width='36px'
                    alignItems='center'
                    style={resolveActiveNavTileStyle(sidebarNav(usersActive))}
                  >
                    <Card>
                      <Tooltip position='right' msg={USERS_ICON.msg}>
                        <Icon
                          name={USERS_ICON.name}
                          variant={resolveNavIconVariant(sidebarNav(usersActive))}
                          modifier='strong'
                          width={USERS_ICON.width}
                          height={USERS_ICON.height}
                          cursor='pointer'
                          onClick={() => navigate(USERS_ICON.route)}
                        />
                      </Tooltip>
                    </Card>
                  </Card>
                </Card>
                );
              })()}

            </Card>
          </Card>
          <div
            style={{
              width: '-webkit-fill-available',
              marginLeft: 'auto',
              marginRight: 'auto',
              overflowY: 'auto',
              minHeight: 0,
            }}
          >
            <Card
              margin={{ top: '7', bottom: '7' }}
              justifyItems='center'
            >
              {children}
            </Card>
          </div>
        </Card>
      </Card>
    </div>
  );
};

export default Layout;